import { useCallback, useEffect, useState } from 'react';
import {
  complianceReferenceApi,
  type ComplianceReferenceCategory,
  type ComplianceReferenceEntry,
  type UpsertComplianceReferenceEntry,
} from '../api/complianceReference';

const categories: Array<{ value: ComplianceReferenceCategory; label: string }> = [
  { value: 'PAYEE_RETURN_REASON', label: 'Payee return reasons' },
  { value: 'GLOBAL_ACCOUNT_RETURN_REASON', label: 'Global account return reasons' },
  { value: 'KYC_RETURN_REASON', label: 'KYC return reasons' },
  { value: 'CLIENT_DECLINE_REASON', label: 'Client decline reasons' },
  { value: 'RESTRICTION_REASON', label: 'Restriction reasons' },
  { value: 'REQUISITES_RETURN_REASON', label: 'Requisites return reasons' },
  { value: 'INDUSTRY', label: 'Industries' },
  { value: 'LEGAL_FORM', label: 'Legal forms' },
  { value: 'APPROVAL_RULE', label: 'Mandatory approval rules' },
];

const emptyEntry = (category: ComplianceReferenceCategory): UpsertComplianceReferenceEntry => ({
  category,
  code: '',
  clientText: '',
  internalDescription: '',
  parentCode: '',
  applicableClientTypes: [],
  isProhibited: false,
  requiresComplianceApproval: false,
  requiresComment: false,
  isActive: true,
  sortOrder: 0,
});

export default function ComplianceReferencePage() {
  const [category, setCategory] = useState<ComplianceReferenceCategory>('KYC_RETURN_REASON');
  const [entries, setEntries] = useState<ComplianceReferenceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<ComplianceReferenceEntry | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<UpsertComplianceReferenceEntry>(() => emptyEntry(category));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setEntries(await complianceReferenceApi.listAdmin(category));
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not load reference entries.');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDraft(emptyEntry(category));
    setEditorOpen(true);
  };

  const openEdit = (entry: ComplianceReferenceEntry) => {
    setEditing(entry);
    setEditorOpen(true);
    setDraft({
      category: entry.category,
      code: entry.code,
      clientText: entry.clientText,
      internalDescription: entry.internalDescription ?? '',
      parentCode: entry.parentCode ?? '',
      applicableClientTypes: entry.applicableClientTypes,
      isProhibited: entry.isProhibited,
      requiresComplianceApproval: entry.requiresComplianceApproval,
      requiresComment: entry.requiresComment,
      isActive: entry.isActive,
      sortOrder: entry.sortOrder,
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setDraft(emptyEntry(category));
    setEditorOpen(false);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (editing) {
        await complianceReferenceApi.update(editing.id, draft);
      } else {
        await complianceReferenceApi.create(draft);
      }
      closeEditor();
      await load();
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not save reference entry.');
    } finally {
      setSaving(false);
    }
  };

  const needsClientTypes = category === 'INDUSTRY' || category === 'LEGAL_FORM' || category === 'APPROVAL_RULE';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Compliance reference data</h1>
          <p className="mt-1 text-sm text-gray-600">Manage client-facing labels, internal codes, industries, legal forms, and approval rules.</p>
        </div>
        <button type="button" onClick={openCreate} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
          Add entry
        </button>
      </div>

      <label className="block max-w-lg text-sm font-medium text-gray-700">
        Catalog
        <select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value as ComplianceReferenceCategory);
            closeEditor();
          }}
          className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          {categories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </label>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      {editorOpen && (
        <section className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5">
          <h2 className="text-lg font-semibold text-gray-900">{editing ? 'Edit entry' : 'Add entry'}</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Internal code">
              <input value={draft.code} disabled={editing !== null} onChange={(event) => setDraft({ ...draft, code: event.target.value })} className="input" placeholder="SOURCE_OF_FUNDS" />
            </Field>
            <Field label="Client-facing text">
              <input value={draft.clientText} onChange={(event) => setDraft({ ...draft, clientText: event.target.value })} className="input" />
            </Field>
            <Field label="Parent code">
              <input value={draft.parentCode ?? ''} onChange={(event) => setDraft({ ...draft, parentCode: event.target.value })} className="input" placeholder={category === 'APPROVAL_RULE' ? 'RISK_LEVEL, JURISDICTION, PEP, INDUSTRY, LEGAL_FORM' : 'Optional parent code'} />
            </Field>
            <Field label="Sort order">
              <input type="number" value={draft.sortOrder} onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) || 0 })} className="input" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Internal description">
                <textarea value={draft.internalDescription ?? ''} onChange={(event) => setDraft({ ...draft, internalDescription: event.target.value })} rows={3} className="input" />
              </Field>
            </div>
            {needsClientTypes && (
              <div className="md:col-span-2 flex flex-wrap gap-5">
                <Toggle label="Business clients" checked={draft.applicableClientTypes.includes('BUSINESS')} onChange={(checked) => setDraft({ ...draft, applicableClientTypes: updateClientTypes(draft.applicableClientTypes, 'BUSINESS', checked) })} />
                <Toggle label="Private clients" checked={draft.applicableClientTypes.includes('PRIVATE')} onChange={(checked) => setDraft({ ...draft, applicableClientTypes: updateClientTypes(draft.applicableClientTypes, 'PRIVATE', checked) })} />
              </div>
            )}
            <div className="md:col-span-2 flex flex-wrap gap-5">
              <Toggle label="Active" checked={draft.isActive} onChange={(checked) => setDraft({ ...draft, isActive: checked })} />
              <Toggle label="Require comment" checked={draft.requiresComment} onChange={(checked) => setDraft({ ...draft, requiresComment: checked })} />
              {(category === 'INDUSTRY' || category === 'LEGAL_FORM') && <Toggle label="Mandatory compliance approval" checked={draft.requiresComplianceApproval} onChange={(checked) => setDraft({ ...draft, requiresComplianceApproval: checked })} />}
              {category === 'INDUSTRY' && <Toggle label="Prohibited industry" checked={draft.isProhibited} onChange={(checked) => setDraft({ ...draft, isProhibited: checked })} />}
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" onClick={closeEditor} disabled={saving} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white">Cancel</button>
            <button type="button" onClick={() => void save()} disabled={saving || !draft.code.trim() || !draft.clientText.trim()} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </section>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Client text</th>
              <th className="px-4 py-3">Flags</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.map((entry) => (
              <tr key={entry.id}>
                <td className="px-4 py-3 font-mono text-xs text-gray-800">{entry.code}</td>
                <td className="px-4 py-3 text-gray-900">
                  <div>{entry.clientText}</div>
                  {entry.parentCode && <div className="mt-0.5 text-xs text-gray-500">Parent: {entry.parentCode}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {[entry.requiresComment && 'Comment', entry.requiresComplianceApproval && 'Approval', entry.isProhibited && 'Prohibited'].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="px-4 py-3">{entry.isActive ? <span className="text-green-700">Active</span> : <span className="text-gray-500">Inactive</span>}</td>
                <td className="px-4 py-3 text-right"><button type="button" onClick={() => openEdit(entry)} className="font-medium text-indigo-700 hover:text-indigo-900">Edit</button></td>
              </tr>
            ))}
            {!loading && entries.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">No entries in this catalog.</td></tr>}
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-500">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-sm font-medium text-gray-700"><span className="mb-1 block">{label}</span>{children}</label>;
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return <label className="inline-flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />{label}</label>;
}

function updateClientTypes(current: string[], value: string, enabled: boolean): string[] {
  return enabled ? Array.from(new Set(current.concat(value))) : current.filter((item) => item !== value);
}
